import { useEffect } from 'react';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';
import { EdgeStack } from '@/react/edge/edge-stacks/types';

import { useUpdateWorkflow } from '../queries/useUpdateWorkflow';
import { Workflow } from '../types';
import { FormValues } from '../wizard/types';
import { WizardStep, useWizardContext } from '../wizard/WizardContext';
import { WizardHeader } from '../wizard/WizardHeader';
import { WizardFooter } from '../wizard/WizardFooter';

import { toEditFormValues } from './toEditFormValues';
import {
  buildUpdateWorkflowPayload,
  isEmptyUpdate,
} from './buildUpdateWorkflowPayload';

type Props = {
  steps: Array<WizardStep>;
  workflow: Workflow;
  edgeStacks: Array<EdgeStack>;
};

function FormContent({ steps }: Pick<Props, 'steps'>) {
  const { currentStep } = useWizardContext();
  const { validateForm } = useFormikContext<FormValues>();

  useEffect(() => {
    validateForm();
  }, [currentStep.id, validateForm]);
  return (
    <Form noValidate>
      <WizardHeader steps={steps} />
      <currentStep.Component />
      <WizardFooter submitLabel="Save" submitLoadingLabel="Saving..." />
    </Form>
  );
}

export function EditForm({ steps, workflow, edgeStacks }: Props) {
  const { currentStep, isLastStep, goToNextStep } = useWizardContext();
  const router = useRouter();
  const updateWorkflowMutation = useUpdateWorkflow();

  const initialFormValues = toEditFormValues(workflow, edgeStacks);

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      validationSchema={currentStep.validateStep}
    >
      <FormContent steps={steps} />
    </Formik>
  );

  function handleSubmit(
    formValues: FormValues,
    { setTouched, setSubmitting }: FormikHelpers<FormValues>
  ) {
    if (!isLastStep) {
      goToNextStep();
      setTouched({});
      setSubmitting(false);
      return;
    }

    const payload = buildUpdateWorkflowPayload(
      formValues,
      initialFormValues,
      workflow.artifacts
    );

    if (isEmptyUpdate(payload)) {
      notifySuccess('No changes', 'There was nothing to save');
      setSubmitting(false);
      goToWorkflow();
      return;
    }

    updateWorkflowMutation.mutate(
      { id: workflow.id, payload },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Workflow updated');
          goToWorkflow();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  }

  function goToWorkflow() {
    router.stateService.go('portainer.gitops.workflows.item', {
      workflowId: workflow.id,
    });
  }
}
